"use client";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGetAgentKowledgeSourceMutation } from '@circuitry-ai/doc-data';
import { CtySpinner } from '../spinner/CtySpinner';
import { checkType, CtyShowToast } from '../toast/CtyToast';
import { getAttachmentPreviewKindFromFileName, getDocumentCategoryLabelFromFileName } from '../utils/commonutils';
import {
  AttachmentAudioLeftIcon,
  AttachmentDocumentLeftIcon,
  AttachmentFileTypeCard,
  AttachmentMediaModal,
  type AttachmentMediaOverlayState,
} from './attachmentFileCardPreview';
import 'viewerjs/dist/viewer.css';
import Viewer from 'viewerjs';

const VIEWER_OPTIONS = { title: false, toolbar: true, navbar: true, movable: true, zoomable: true, rotatable: true };

/** Module-level cache keyed by the requested source_id set, so re-renders, StrictMode's double
 * effect invocation, and repeat mounts of the same attachment group share one in-flight/resolved
 * request instead of re-fetching. */
const presignedUrlRequests = new Map<string, Promise<Record<string, string>>>();

type PresignedUrlSource = { source_id: string; file_name: string };
type GetAgentPresignedUrlsTrigger = ReturnType<typeof useGetAgentKowledgeSourceMutation>[0];

function resolvePresignedUrls(
  sources: PresignedUrlSource[],
  getAgentPresignedUrls: GetAgentPresignedUrlsTrigger
): Promise<Record<string, string>> {
  const key = sources.map((s) => s.source_id).sort((a, b) => a.localeCompare(b)).join(',');
  let request = presignedUrlRequests.get(key);
  if (!request) {
    const payload = {
      source_ids: sources.map((s) => ({
        source_id: s.source_id,
        filename: s.file_name,
        source_type: 'advisor',
        file_extension: (s.file_name.split('.').pop() || '').toLowerCase(),
      })),
    };
    request = getAgentPresignedUrls(payload as any)
      .unwrap()
      .then((res: any) => {
        const result: Record<string, string> = {};
        (res?.data || []).forEach((d: any) => {
          if (d?.source_id && d?.full_source_presigned_url) result[d.source_id] = d.full_source_presigned_url;
        });
        return result;
      });
    request.catch(() => presignedUrlRequests.delete(key)); // allow retry after a failed fetch
    presignedUrlRequests.set(key, request);
  }
  return request;
}

function useImageViewer(imageUrls: string[]) {
  const viewerContainerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<InstanceType<typeof Viewer> | null>(null);
  useEffect(() => {
    if (imageUrls.length === 0 || !viewerContainerRef.current) return;
    const timerId = setTimeout(() => {
      if (!viewerContainerRef.current) return;
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
      try {
        viewerRef.current = new Viewer(viewerContainerRef.current, VIEWER_OPTIONS);
      } catch (err) {
        console.warn('Viewer init failed:', err);
      }
    }, 100);
    return () => {
      clearTimeout(timerId);
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
    };
  }, [imageUrls]);
  const openEnlargedAt = (index: number) => {
    if (viewerRef.current) {
      try {
        viewerRef.current.view(index);
      } catch (err) {
        console.warn('Viewer view failed:', err);
      }
    }
  };
  return { viewerContainerRef, openEnlargedAt };
}

/**
 * Renders attachments referenced only by `source_id`/`file_name` (e.g. comment history, triage feedback)
 * the same way they appear in advisor conversations: images open in the enlarge viewer, video/audio open
 * a media modal, and documents open in a new tab — all resolved via presigned URLs.
 */
export function AttachmentSourcesPreview(props: Readonly<{
  sources: { source_id: string; file_name: string }[];
  /** Pre-resolved presigned URLs keyed by source_id; skips the lookup fetch when provided. */
  presignedUrls?: Record<string, string>;
}>) {
  const { sources, presignedUrls } = props;
  const [getAgentPresignedUrls] = useGetAgentKowledgeSourceMutation();
  const [urlsBySourceId, setUrlsBySourceId] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);
  const [mediaOverlay, setMediaOverlay] = useState<AttachmentMediaOverlayState>(null);

  // Keyed by content (not array identity) so callers passing a fresh literal each render
  // (e.g. `sources={entry.sources ?? []}`) don't re-trigger the presigned-URL fetch below.
  const sourcesKey = (sources ?? []).map((s) => `${s?.source_id ?? ''}:${s?.file_name ?? ''}`).join('|');
  const validSources = useMemo(
    () => (sources ?? []).filter((s) => s?.source_id && s?.file_name),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sourcesKey]
  );

  const resolved = presignedUrls ?? urlsBySourceId;

  const imageSourcesWithUrl = useMemo(
    () =>
      validSources.filter(
        (s) => getAttachmentPreviewKindFromFileName(s.file_name) === 'image' && !!resolved[s.source_id]
      ),
    [validSources, resolved]
  );
  const imageUrlsOrdered = useMemo(
    () => imageSourcesWithUrl.map((s) => resolved[s.source_id]),
    [imageSourcesWithUrl, resolved]
  );
  const { viewerContainerRef, openEnlargedAt } = useImageViewer(imageUrlsOrdered);

  useEffect(() => {
    if (presignedUrls != null || !validSources.length || failed) return;
    let cancelled = false;
    resolvePresignedUrls(validSources, getAgentPresignedUrls)
      .then((next) => {
        if (!cancelled && Object.keys(next).length > 0) {
          setUrlsBySourceId((prev) => ({ ...prev, ...next }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          CtyShowToast(checkType.error, 'Failed to load attachment');
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presignedUrls, validSources, failed, getAgentPresignedUrls]);

  if (!validSources.length) return null;

  const renderCell = (s: { source_id: string; file_name: string }) => {
    const url = resolved[s.source_id];
    const kind = getAttachmentPreviewKindFromFileName(s.file_name);
    const loading = !url && !failed;

    if (kind === 'image') {
      if (loading) {
        return (
          <div className="w-12 h-12 rounded-md border border-default-200 bg-default-100 flex-shrink-0 flex items-center justify-center" aria-label="Loading attachment">
            <CtySpinner size="sm" />
          </div>
        );
      }
      if (!url) return null;
      const imageIndex = imageSourcesWithUrl.findIndex((x) => x.source_id === s.source_id);
      return (
        <button
          type="button"
          onClick={() => imageIndex >= 0 && openEnlargedAt(imageIndex)}
          className="w-12 h-12 rounded-md border border-default-200 overflow-hidden bg-default-100 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <img src={url} alt="" className="w-full h-full object-cover" />
        </button>
      );
    }

    if (kind === 'video') {
      if (!loading && !url) return null;
      return (
        <AttachmentFileTypeCard
          label="Video"
          fileName={s.file_name}
          loading={loading}
          onClick={() => url && setMediaOverlay({ kind: 'video', url, name: s.file_name })}
          leftVariant="media"
          leftContent={
            url ? (
              <video src={url} className="w-full h-full object-cover pointer-events-none" muted playsInline preload="metadata" />
            ) : null
          }
        />
      );
    }

    if (kind === 'audio') {
      if (!loading && !url) return null;
      return (
        <AttachmentFileTypeCard
          label="Audio"
          fileName={s.file_name}
          loading={loading}
          onClick={() => url && setMediaOverlay({ kind: 'audio', url, name: s.file_name })}
          leftVariant="neutral"
          leftContent={<AttachmentAudioLeftIcon />}
        />
      );
    }

    const docLabel = getDocumentCategoryLabelFromFileName(s.file_name);
    if (!loading && !url) return null;
    return (
      <AttachmentFileTypeCard
        label={docLabel}
        fileName={s.file_name}
        loading={loading}
        onClick={() => url && globalThis.open(url, '_blank', 'noopener,noreferrer')}
        leftVariant="neutral"
        leftContent={<AttachmentDocumentLeftIcon />}
      />
    );
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-2 mt-1 items-start">
        {validSources.map((s) => (
          <React.Fragment key={s.source_id}>{renderCell(s)}</React.Fragment>
        ))}
      </div>
      {imageUrlsOrdered.length > 0 && (
        <div ref={viewerContainerRef as React.RefObject<HTMLDivElement>} className="hidden" aria-hidden="true">
          {imageUrlsOrdered.map((u) => (
            <img key={u} src={u} alt="" />
          ))}
        </div>
      )}
      <AttachmentMediaModal overlay={mediaOverlay} onClose={() => setMediaOverlay(null)} />
    </>
  );
}
